/**
 * Legacy API Request Module
 *
 * Core request function for the legacy ATF Vision API (https://app.atfvision.net.au/api).
 * These endpoints don't require authentication and are used for pre-login flows.
 */

import { getAppConfig } from "@src/config/legacyKitConfig";

function getLegacyApiBaseUrl(): string {
  const config = getAppConfig();
  const baseUrl = `${config.apiBaseUrl}/api`;
  return baseUrl;
}

// Error types
export class LegacyApiError extends Error {
  readonly kind = "http" as const;

  constructor(
    public status: number,
    public statusText: string,
    public body?: string,
  ) {
    super(`Legacy API Error: ${status} ${statusText}`);
    this.name = "LegacyApiError";
  }
}

export class NetworkError extends Error {
  readonly kind = "network" as const;
  readonly status = 0;

  constructor(message = "Network request failed") {
    super(message);
    this.name = "NetworkError";
  }
}

export class ValidationError extends Error {
  readonly kind = "validation" as const;

  constructor(
    message: string,
    public field?: string,
  ) {
    super(message);
    this.name = "ValidationError";
  }
}

export class RateLimitError extends Error {
  readonly kind = "rate_limit" as const;
  readonly status = 429;

  constructor(message = "Too many requests, please try again later.") {
    super(message);
    this.name = "RateLimitError";
  }
}

interface LegacyApiRequestOptions {
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  path: string;
  body?: unknown;
  logMessage?: string;
}

/**
 * Core request function for legacy API calls.
 * Does not include authentication headers.
 */
export async function legacyApiRequest<T>(options: LegacyApiRequestOptions): Promise<T> {
  const { method, path, body, logMessage } = options;

  const url = `${getLegacyApiBaseUrl()}${path}`;

  if (__DEV__) {
    console.log(`[LegacyAPI] ${method} ${url}`);
    if (logMessage) {
      console.log(logMessage, url);
    }
  }

  const fetchOptions: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  };

  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, fetchOptions);

    if (__DEV__) {
      console.log(`[LegacyAPI] Response status: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
      const errorText = await response.text();
      if (__DEV__) {
        console.error(`[LegacyAPI] Error response body:`, errorText);
      }
      throw new LegacyApiError(response.status, response.statusText, errorText);
    }

    // Handle empty responses (some endpoints return 200 with no body)
    const text = await response.text();

    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch (parseError) {
      if (__DEV__) {
        console.error(`[LegacyAPI] JSON parse error. Response text:`, text);
      }
      throw parseError;
    }
  } catch (error) {
    // Re-throw our custom errors
    if (error instanceof LegacyApiError || error instanceof ValidationError || error instanceof RateLimitError) {
      throw error;
    }

    if (__DEV__) {
      console.error("[LegacyAPI] Network error:", error);
    }

    // Wrap network/fetch errors
    throw new NetworkError("Unable to connect to server");
  }
}
